import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewChecked, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, BookingMessage } from '@app/core/services/message.service';
import { ToastService } from '@app/core/services/toast.service';

@Component({
  selector: 'app-message-chat',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div>
      <div #messageSection class="bg-white rounded-lg shadow-lg">
      <!-- Header with Toggle -->
      <button 
        (click)="toggleMessages()" 
        class="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4 rounded-t-lg hover:from-purple-700 hover:to-purple-800 transition flex items-center justify-between">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
          </svg>
          <div class="text-left">
            <h3 class="text-lg font-bold">
              {{ isAdmin ? 'Kommunikation' : 'Nachrichten zur Buchung' }}
            </h3>
            <p class="text-sm opacity-90" *ngIf="!isAdmin && !isExpanded">
              Stellen Sie Fragen oder tauschen Sie sich mit unserem Team aus
            </p>
          </div>
        </div>
        
        <div class="flex items-center gap-3">
          <!-- Unread Badge -->
          <span *ngIf="unreadCount > 0 && !isExpanded" 
                class="px-3 py-1.5 text-sm font-bold bg-red-500 text-white rounded-full animate-pulse shadow-lg">
            {{ unreadCount }}
          </span>
          
          <svg class="w-6 h-6 transition-transform" [class.rotate-180]="isExpanded" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </button>

      <!-- Collapsible Content -->
      <div *ngIf="isExpanded" class="border-t border-gray-200">
        <!-- Input Area (moved to top) -->
        <div class="p-4 bg-gray-50 border-b">
          <!-- Admin: Toggle for Internal Notes -->
          <div *ngIf="isAdmin" class="mb-3">
            <label class="inline-flex items-center cursor-pointer">
              <input type="checkbox" [(ngModel)]="isInternalNote" class="sr-only peer">
              <div class="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              <span class="ms-3 text-sm font-medium" [ngClass]="isInternalNote ? 'text-orange-600' : 'text-gray-700'">
                {{ isInternalNote ? '🔒 Interne Notiz (nur für Admins)' : '💬 Antwort an Kunde' }}
              </span>
            </label>
          </div>

          <!-- File Preview -->
          <div *ngIf="selectedFiles.length > 0" class="mb-3 flex flex-wrap gap-2">
            <div *ngFor="let file of selectedFiles; let i = index" 
                 class="flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
              </svg>
              <span class="truncate max-w-[150px]">{{ file.name }}</span>
              <button (click)="removeFile(i)" class="hover:text-purple-900">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>

          <div class="flex gap-2">
            <!-- File Upload Button -->
            <label class="cursor-pointer flex items-center justify-center w-10 h-10 bg-gray-200 hover:bg-gray-300 rounded-lg transition">
              <input #fileInput type="file" multiple class="hidden" (change)="onFileSelected($event)" accept="image/*,application/pdf,.doc,.docx">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
              </svg>
            </label>

            <!-- Text Input -->
            <textarea
              [(ngModel)]="newMessage"
              (keydown.enter)="onEnterKey($any($event))"
              placeholder="Nachricht eingeben..."
              rows="2"
              class="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              [disabled]="sending"
            ></textarea>

            <!-- Send Button -->
            <button
              (click)="sendMessage()"
              [disabled]="!newMessage.trim() || sending"
              class="flex items-center justify-center w-12 h-12 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
              <svg *ngIf="!sending" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
              </svg>
              <div *ngIf="sending" class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            </button>
          </div>

          <p class="text-xs text-gray-500 mt-2">
            {{ isAdmin 
              ? (isInternalNote ? 'Interne Notiz wird nicht an den Kunden gesendet' : 'Kunde erhält eine Email-Benachrichtigung') 
              : 'Drücken Sie Enter zum Senden oder Shift+Enter für eine neue Zeile' }}
          </p>
        </div>

        <!-- Messages Container (no max-height, inherits from parent) -->
        <div #messagesContainer class="p-4 space-y-4 bg-gray-50">
          <div *ngIf="loading" class="flex justify-center items-center py-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>

          <div *ngIf="!loading && visibleMessages.length === 0" class="flex flex-col items-center justify-center py-12 text-gray-500">
            <svg class="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
            <p class="text-lg font-medium">Noch keine Nachrichten</p>
            <p class="text-sm">{{ isAdmin ? 'Der Kunde hat noch keine Nachrichten gesendet' : 'Starten Sie die Konversation' }}</p>
          </div>

        <!-- Message Comments (Full Width) -->
        <ng-container *ngIf="!loading">
        <div *ngFor="let message of visibleMessages" 
             class="w-full">
          <!-- Internal Note Badge (Admin only) -->
          <div *ngIf="message.type === 2" class="flex items-center gap-2 mb-2 text-xs text-orange-600">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
            </svg>
            <span class="font-semibold">INTERNE NOTIZ</span>
          </div>

          <div class="w-full rounded-lg border shadow-sm"
               [ngClass]="{
                 'bg-white border-gray-200': isMessageFromCustomer(message),
                 'bg-purple-50 border-purple-200': message.type === 1,
                 'bg-orange-50 border-orange-300 border-2': message.type === 2
               }">
            <!-- Header with Sender Info -->
            <div class="flex items-center justify-between px-4 py-3 border-b"
                 [ngClass]="{
                   'border-gray-100': isMessageFromCustomer(message),
                   'border-purple-100': message.type === 1,
                   'border-orange-200': message.type === 2
                 }">
              <div class="flex items-center gap-3">
                <!-- Avatar Icon -->
                <div class="flex items-center justify-center w-8 h-8 rounded-full"
                     [ngClass]="{
                       'bg-purple-200 text-purple-700': isMessageFromCustomer(message),
                       'bg-purple-600 text-white': message.type === 1,
                       'bg-orange-300 text-orange-800': message.type === 2
                     }">
                  <svg *ngIf="isMessageFromCustomer(message)" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path>
                  </svg>
                  <svg *ngIf="!isMessageFromCustomer(message)" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clip-rule="evenodd"></path>
                  </svg>
                </div>
                
                <div>
                  <div class="flex items-center gap-2">
                    <span class="font-semibold text-gray-900">{{ message.senderName }}</span>
                    <span *ngIf="message.type === 1" class="px-2 py-0.5 text-xs font-medium bg-purple-600 text-white rounded-full">
                      Team
                    </span>
                    <span *ngIf="message.type === 0" class="px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-full">
                      Kunde
                    </span>
                  </div>
                  <span class="text-xs text-gray-500">{{ formatDate(message.createdAt) }}</span>
                </div>
              </div>
              
              <!-- Read Status (Admin view only) -->
              <div *ngIf="isAdmin && message.type === 0" class="text-xs">
                <span *ngIf="message.isReadByAdmin" class="text-gray-500">✓ Gelesen</span>
                <span *ngIf="!message.isReadByAdmin" class="font-bold text-purple-600">● Neu</span>
              </div>
            </div>

            <!-- Message Content -->
            <div class="px-4 py-3">
              <p class="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">{{ message.content }}</p>
              
              <!-- Attachments -->
              <div *ngIf="message.attachments && message.attachments.length > 0" class="mt-3 space-y-2">
                <div *ngFor="let attachment of message.attachments" 
                     class="flex items-center gap-2 text-xs p-2 rounded bg-gray-100 hover:bg-gray-200 transition">
                  <svg class="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
                  </svg>
                  <a [href]="attachment.fileUrl" target="_blank" class="underline flex-1 truncate text-purple-700 hover:text-purple-900">
                    {{ attachment.fileName }}
                  </a>
                  <span class="text-gray-500">({{ formatFileSize(attachment.fileSize) }})</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </ng-container>
        </div>
      </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class MessageChatComponent implements OnInit, AfterViewChecked {
  @Input() bookingId!: string;
  @Input() token?: string; // For customer view
  @Input() isAdmin: boolean = false;
  @Output() unreadCountChange = new EventEmitter<number>();
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('messageSection') private messageSection?: ElementRef;

  messages: BookingMessage[] = [];
  newMessage: string = '';
  loading: boolean = false;
  sending: boolean = false;
  isInternalNote: boolean = false;
  selectedFiles: File[] = [];
  isExpanded: boolean = false;
  unreadCount: number = 0;
  private markAsReadTimeout?: any;
  private shouldScrollToBottom = false;

  constructor(
    private messageService: MessageService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    console.log('[MessageChat] ngOnInit - bookingId:', this.bookingId, 'token:', this.token, 'isAdmin:', this.isAdmin);
    
    // Safety timeout - if loading doesn't complete in 10 seconds, force it to false
    setTimeout(() => {
      if (this.loading) {
        console.error('[MessageChat] TIMEOUT: Loading still true after 10 seconds - forcing to false');
        this.loading = false;
      }
    }, 10000);
    
    this.loadMessages();
    // Don't mark as read on init - only when expanded for 3 seconds
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  get visibleMessages(): BookingMessage[] {
    // MessageType enum: 0 = CustomerMessage, 1 = AdminReply, 2 = InternalNote
    let filtered = this.isAdmin 
      ? this.messages 
      : this.messages.filter(m => m.type !== 2); // Filter InternalNote
    
    // Sort descending (newest first)
    return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  scrollToMessages(): void {
    // Expand if not already expanded
    if (!this.isExpanded) {
      this.isExpanded = true;
      
      // Wait for DOM to update, then scroll
      setTimeout(() => {
        if (this.messageSection) {
          this.messageSection.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
      
      // Start the 3-second timer for mark as read
      this.markAsReadTimeout = setTimeout(() => {
        this.markAsRead();
        this.updateUnreadCount();
      }, 3000);
    }
  }

  toggleMessages(): void {
    this.isExpanded = !this.isExpanded;
    
    if (this.isExpanded) {
      // Clear any existing timeout
      if (this.markAsReadTimeout) {
        clearTimeout(this.markAsReadTimeout);
      }
      
      // Mark as read after 3 seconds
      this.markAsReadTimeout = setTimeout(() => {
        this.markAsRead();
        this.updateUnreadCount();
      }, 3000);
    } else {
      // Cancel mark as read if collapsed before 3 seconds
      if (this.markAsReadTimeout) {
        clearTimeout(this.markAsReadTimeout);
        this.markAsReadTimeout = undefined;
      }
    }
  }

  updateUnreadCount(): void {
    console.log('[MessageChat] updateUnreadCount - isAdmin:', this.isAdmin);
    console.log('[MessageChat] All messages:', this.messages);
    
    // Log all message types
    const messageTypes = this.messages.map(m => ({ type: m.type, sender: m.senderName, isReadByAdmin: m.isReadByAdmin, isReadByCustomer: m.isReadByCustomer }));
    console.log('[MessageChat] Message types:', messageTypes);
    
    // MessageType enum: 0 = CustomerMessage, 1 = AdminReply, 2 = InternalNote
    if (this.isAdmin) {
      const customerMessages = this.messages.filter(m => m.type === 0);
      const unread = customerMessages.filter(m => !m.isReadByAdmin);
      console.log('[MessageChat] Admin - CustomerMessages:', customerMessages);
      console.log('[MessageChat] Admin - Unread messages:', unread);
      this.unreadCount = unread.length;
    } else {
      const adminReplies = this.messages.filter(m => m.type === 1);
      const unread = adminReplies.filter(m => !m.isReadByCustomer);
      console.log('[MessageChat] Customer - AdminReplies:', adminReplies);
      console.log('[MessageChat] Customer - Unread messages:', unread);
      this.unreadCount = unread.length;
    }
    
    console.log('[MessageChat] Final unreadCount:', this.unreadCount);
    console.log('[MessageChat] isExpanded:', this.isExpanded);
    this.unreadCountChange.emit(this.unreadCount);
  }

  loadMessages(): void {
    console.log('[MessageChat] Loading messages for booking:', this.bookingId, 'isAdmin:', this.isAdmin);
    this.loading = true;
    const request = this.isAdmin
      ? this.messageService.getAllMessages(this.bookingId)
      : this.messageService.getMessages(this.bookingId, this.token!);

    request.subscribe({
      next: (messages) => {
        console.log('[MessageChat] Received messages:', messages);
        this.messages = messages.map(m => ({
          ...m,
          createdAt: new Date(m.createdAt)
        }));
        this.updateUnreadCount();
        console.log('[MessageChat] Mapped messages:', this.messages);
        console.log('[MessageChat] Visible messages:', this.visibleMessages);
        this.updateUnreadCount();
        console.log('[MessageChat] Unread count after update:', this.unreadCount);
        this.loading = false;
        this.shouldScrollToBottom = true;
        this.cdr.detectChanges(); // Force change detection
        console.log('[MessageChat] Change detection triggered');
      },
      error: (error) => {
        console.error('[MessageChat] Error loading messages:', error);
        this.toastService.error('Fehler beim Laden der Nachrichten');
        this.loading = false;
        this.cdr.detectChanges();
      },
      complete: () => {
        console.log('[MessageChat] Loading complete, loading state:', this.loading);
      }
    });
  }

  sendMessage(): void {
    if (!this.newMessage.trim() && this.selectedFiles.length === 0) return;

    this.sending = true;
    const content = this.newMessage.trim();
    const files = this.selectedFiles.length > 0 ? this.selectedFiles : undefined;

    const request = this.isAdmin
      ? this.messageService.addAdminMessage(this.bookingId, content, this.isInternalNote, undefined, undefined, files)
      : this.messageService.addCustomerMessage(this.bookingId, this.token!, content, files);

    request.subscribe({
      next: (message) => {
        this.messages.push({
          ...message,
          createdAt: new Date(message.createdAt)
        });
        this.updateUnreadCount();
        this.newMessage = '';
        this.selectedFiles = [];
        this.sending = false;
        this.shouldScrollToBottom = true;
        this.cdr.detectChanges();
        this.toastService.success('Nachricht gesendet');
        
        if (this.fileInput) {
          this.fileInput.nativeElement.value = '';
        }
      },
      error: (error) => {
        console.error('Error sending message:', error);
        this.toastService.error('Fehler beim Senden der Nachricht');
        this.sending = false;
        this.cdr.detectChanges();
      }
    });
  }

  markAsRead(): void {
    if (this.isAdmin) {
      this.messageService.markMessagesAsReadByAdmin(this.bookingId).subscribe({
        next: () => {
          // Update local messages as read
          this.messages.forEach(m => {
            if (m.type === 'CustomerMessage') {
              m.isReadByAdmin = true;
            }
          });
          this.updateUnreadCount();
          this.cdr.detectChanges();
        }
      });
    } else if (this.token) {
      this.messageService.markMessagesAsReadByCustomer(this.bookingId, this.token).subscribe({
        next: () => {
          // Update local messages as read
          this.messages.forEach(m => {
            if (m.type === 'AdminReply') {
              m.isReadByCustomer = true;
            }
          });
          this.updateUnreadCount();
          this.cdr.detectChanges();
        }
      });
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const newFiles = Array.from(input.files);
      this.selectedFiles.push(...newFiles);
      
      // Check total file size (max 50MB)
      const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > 50 * 1024 * 1024) {
        this.toastService.error('Maximale Dateigröße überschritten (50 MB)');
        this.selectedFiles = [];
        input.value = '';
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  onEnterKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  isMessageFromCustomer(message: BookingMessage): boolean {
    // MessageType enum: 0 = CustomerMessage
    return message.type === 0;
  }

  formatDate(date: Date): string {
    const now = new Date();
    const messageDate = new Date(date);
    const diffMs = now.getTime() - messageDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;

    return messageDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }
}
